/**
 * Centralized logger for the application.
 * Prevents console leaks in production by only logging when in development mode.
 * When Sentry is configured, `error()` also reports to Sentry Cloud.
 *
 * Usage:
 * import { logger } from '@/shared/lib/logger';
 * logger.error('Something went wrong', err);
 */

import { sentry } from '@/shared/lib/sentry';

export const logger = {
  log: (...args: unknown[]) => {
    if (import.meta.env.DEV) {
      console.log(...args);
    }
  },
  warn: (...args: unknown[]) => {
    if (import.meta.env.DEV) {
      console.warn(...args);
    }
  },
  error: (...args: unknown[]) => {
    if (import.meta.env.DEV) {
      console.error(...args);
    }
    // Report the first Error-like argument to Sentry.
    const err = args.find((a): a is Error => a instanceof Error);
    const context =
      args.filter(
        (a): a is Record<string, unknown> =>
          !!a && typeof a === 'object' && !(a instanceof Error),
      )[0] ?? undefined;
    sentry.captureException(err ?? new Error(String(args[0])), context);
  },
  info: (...args: unknown[]) => {
    if (import.meta.env.DEV) {
      console.info(...args);
    }
  },
};
